import { z } from 'zod/v4'

import { defineScreen, type ScreenData } from '../define-screen.js'
import { freshnessSchema } from '../freshness.js'
import { screenSection } from '../section.js'

/*
 * «Exchanges» › the whole platform catalogue with what the table paints and the edit form preloads, and the three
 * catalogues the form offers (exchange types, blockchain networks, asset tickers). The page does not paginate: it
 * filters in the browser. Each schema mirrors what the domain returns (`loadAdminExchangeCatalogue`,
 * `readActiveExchangeTypes`, `toSelectableNetworkOptions`, `toAssetSymbolOptions`) and the type assertions of
 * `packages/backend/src/platform/server/__tests__/shared-exchanges-screen.type-assertions.ts` tie them.
 */

/** The list is read whole up to here; beyond it the page says «Se muestran N de M». */
export const ADMIN_EXCHANGES_CAP = 500

/**
 * The runtime-governing columns of an exchange (#2252), in ONE place: the row of this screen and the schema of the old
 * route `GET /api/admin/exchanges` (`exchanges/api/swagger.ts`, as a plain object) declare them with this shape.
 * `chainId`, `nativeAsset` and `brandColor` are also what `/api/config` serves to the customer app; `isMarginPlatform`
 * is read-only on purpose (its reader has no production caller, so the panel reports the stored value and refuses to
 * set it).
 */
export const adminExchangeRuntimeShape = {
  supportsCsvImport: z.boolean(),
  supportsApiImport: z.boolean(),
  chainId: z.number().int().nullable(),
  nativeAsset: z.string().nullable(),
  brandColor: z.string().nullable(),
  requiresPassphrase: z.boolean(),
  fetchConcurrency: z.number().int(),
  isMarginPlatform: z.boolean(),
}

export const adminExchangeRowSchema = z.strictObject({
  slug: z.string(),
  name: z.string(),
  /** Only for the search: the one of the old route matches `name`, `display_name` and the slug. It is not painted. */
  displayName: z.string().nullable(),
  type: z.string(),
  logoUrl: z.string(),
  isActive: z.boolean(),
  isShipped: z.boolean(),
  templateVersion: z.string().nullable(),
  rulesCount: z.number().int(),
  aliases: z.array(z.string()),
  apiDocsUrl: z.string().nullable(),
  adapterAvailable: z.boolean(),
  /** Insolvency (claims): the catalogue's, or an operator's statement (`insolvencyStated`). */
  insolventOn: z.string().nullable(),
  insolvencyProceeding: z.string().nullable(),
  insolvencyStated: z.boolean(),
  /** Custody country: the catalogue's, or an operator's statement. */
  custodianCountry: z.string().nullable(),
  custodianCountryStated: z.boolean(),
  ...adminExchangeRuntimeShape,
})

export const adminExchangeTypeOptionSchema = z.strictObject({
  key: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  sortOrder: z.number().int(),
})

export const adminExchangeNetworkOptionSchema = z.strictObject({
  chainId: z.number().int(),
  slug: z.string(),
  displayName: z.string(),
  nativeToken: z.string(),
})

export const adminExchangesScreen = defineScreen({
  id: 'admin.exchanges',
  path: '/api/screens/admin/exchanges',
  access: 'super_admin',
  query: z.strictObject({}),
  response: z.strictObject({
    exchanges: screenSection(
      z.strictObject({ rows: z.array(adminExchangeRowSchema), total: z.number().int() }),
    ),
    exchangeTypes: screenSection(z.array(adminExchangeTypeOptionSchema)),
    networks: screenSection(z.array(adminExchangeNetworkOptionSchema)),
    assetSymbols: screenSection(z.array(z.string())),
    freshness: freshnessSchema,
  }),
  dependsOn: ['admin.exchanges'],
  budget: { requests: 1, statements: 16, bytes: 250_000, p95Ms: 300 },
  summary:
    'Exchanges: the whole platform catalogue, the exchange types, the networks and the asset tickers of its form',
})

export type AdminExchangeRow = z.infer<typeof adminExchangeRowSchema>
export type AdminExchangeTypeOption = z.infer<typeof adminExchangeTypeOptionSchema>
export type AdminExchangeNetworkOption = z.infer<typeof adminExchangeNetworkOptionSchema>
export type AdminExchangesData = ScreenData<typeof adminExchangesScreen>

/**
 * The networks an exchange may be attached to, as the form offers them in «Cadena»: the enabled ones with a real chain
 * id (`__unmapped__`, −1, is a quarantine and not a chain), by chain id. ONE rule for the server (the screen) and for
 * whoever still reads the networks route in the browser; it neither changes its input nor adds a key.
 */
export function toSelectableNetworkOptions(
  networks: readonly (AdminExchangeNetworkOption & { enabled: boolean })[],
): AdminExchangeNetworkOption[] {
  return networks
    .filter((network) => network.enabled && network.chainId >= 1)
    .map((network) => ({
      chainId: network.chainId,
      slug: network.slug,
      displayName: network.displayName,
      nativeToken: network.nativeToken,
    }))
    .sort((left, right) => left.chainId - right.chainId)
}
